import {useRef, useState} from 'react';
import {reportsApi} from '../api/reports.api';
import {ReportDocument, ReportRunOverrides, ReportTemplate} from '../types/report-builder.types';
import {ReportDownloadState} from '../types/report-list.types';
import {useReportError} from './use-report-error';
import {useReportPermissions} from './use-report-permissions';

export const useReportDownload = () => {
  const [state, setState] = useState<ReportDownloadState>({});
  const inFlight = useRef(false);
  const pendingDocuments = useRef(new Map<number, ReportDocument>());
  const reportError = useReportError();
  const {canView, canDownload} = useReportPermissions();

  const run = async (report: ReportTemplate, overrides?: ReportRunOverrides) => {
    if (inFlight.current || !canView || !canDownload) return;
    inFlight.current = true;
    // Explicit values start a new generation; a download retry reuses its existing document.
    let document = overrides === undefined ? pendingDocuments.current.get(report.id) : undefined;
    if (overrides !== undefined) pendingDocuments.current.delete(report.id);
    let missingTemplate = false;
    let completed = false;
    try {
      if (!document) {
        setState({reportId: report.id, phase: 'generating'});
        document = await reportsApi.generate(report.id, overrides);
        pendingDocuments.current.set(report.id, document);
      }
      setState({reportId: report.id, phase: 'downloading', document});
      await reportsApi.download(document);
      pendingDocuments.current.delete(report.id);
      completed = true;
      setState({reportId: report.id, completed: true});
    } catch (error) {
      const result = await reportError(
        error,
        document
          ? 'Не удалось скачать отчёт. Повторите скачивание.'
          : 'Не удалось сформировать отчёт. Попробуйте ещё раз.',
      );
      missingTemplate = result.code === '4007';
      if (result.code === '4001' || result.code === '4010') {
        pendingDocuments.current.delete(report.id);
        document = undefined;
      }
      setState({reportId: report.id, error: result.message, document});
    } finally {
      inFlight.current = false;
    }
    return {missingTemplate, completed, document};
  };

  return {
    state,
    run,
    busy: Boolean(state.phase),
    getPendingDocument: (id: number) => pendingDocuments.current.get(id),
    clear: () => setState({}),
  };
};
